// Frontend service for "Prime Agent Direct Connect" — linking banks that Plaid
// cannot reach ("Direct Connect"). Focus's backend sends a one-shot request TO
// Prime Agent (Spencer's browser-automation agent) to log into the bank, download the
// transaction export, and hand it back for import. No Prime Agent-side polling.
//
// Mirrors the auth/API-root conventions of plaidService.ts. All calls hit the
// backend /api/prime-agent/* routes — the backend holds the encrypted bank
// credentials and talks to Prime Agent; the browser never sees Prime Agent directly.

import { supabase } from "../supabase/client";
import { isAbortError } from "../utils/abort";
import { getCachedAccessToken } from "../supabase/authStorage";

const resolveApiRoot = () => {
  if (typeof window !== "undefined") {
    const isLocal =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";
    if (isLocal) {
      return "/api";
    }
  }
  return process.env.REACT_APP_API_URL
    ? `${process.env.REACT_APP_API_URL}/api`
    : "/api";
};

const API_ROOT = resolveApiRoot();

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const cachedToken = getCachedAccessToken();
  if (cachedToken) {
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${cachedToken}`,
    };
  }
  let session = null;
  try {
    const {
      data: { session: activeSession },
    } = await supabase.auth.getSession();
    session = activeSession;
  } catch (error) {
    if (!isAbortError(error)) {
      console.error("[prime-agent] getAuthHeaders getSession failed", error);
    }
  }
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (session?.access_token) {
    headers["Authorization"] = `Bearer ${session.access_token}`;
  }
  return headers;
};

export interface PrimeAgentConnection {
  connected: boolean;
  status?: "connected" | "disconnected" | "error";
  prime_agent_account_ref?: string | null;
  verified_at?: string | null;
}

export interface PrimeAgentBankLink {
  id: string;
  institution_id: string;
  institution_name: string;
  // Never includes credentials — those stay encrypted server-side.
  last_sync_at?: string | null;
  last_status?: "ok" | "error" | "needs_otp" | "needs_human" | "never" | null;
  last_error?: string | null;
}

export interface PrimeAgentProvider {
  id: string;
  name: string;
  color?: string;
  loginUrl?: string;
}

// Result of a sync request. "needs_otp" means Prime Agent hit a 2FA challenge and we
// must relay a code the user reads off their own phone/email back through Focus.
export interface PrimeAgentSyncResult {
  status: "ok" | "needs_otp" | "needs_human" | "error";
  imported?: number;
  reason?: string;
}

const request = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_ROOT}${path}`, {
    ...init,
    headers: { ...headers, ...(init?.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(
      (body && (body.error || body.message)) || `Request failed (${res.status})`
    );
  }
  return body as T;
};

export const primeAgentService = {
  // Whether the user has connected their Prime Agent account.
  async getConnection(): Promise<PrimeAgentConnection> {
    try {
      return await request<PrimeAgentConnection>("/prime-agent/connection");
    } catch {
      return { connected: false, status: "disconnected" };
    }
  },

  // Link the user's Prime Agent account to Focus.
  async connect(primeAgentAccountRef?: string): Promise<PrimeAgentConnection> {
    return request<PrimeAgentConnection>("/prime-agent/connect", {
      method: "POST",
      body: JSON.stringify({ prime_agent_account_ref: primeAgentAccountRef ?? null }),
    });
  },

  async disconnect(): Promise<{ success: boolean }> {
    return request<{ success: boolean }>("/prime-agent/disconnect", {
      method: "POST",
    });
  },

  // Supported Direct-Connect institutions.
  async listProviders(): Promise<PrimeAgentProvider[]> {
    try {
      const { providers } = await request<{ providers: PrimeAgentProvider[] }>(
        "/prime-agent/providers"
      );
      return providers || [];
    } catch {
      return [];
    }
  },

  async listBankLinks(): Promise<PrimeAgentBankLink[]> {
    try {
      const { links } = await request<{ links: PrimeAgentBankLink[] }>(
        "/prime-agent/bank-links"
      );
      return links || [];
    } catch {
      return [];
    }
  },

  // Add a Direct-Connect bank. Credentials are POSTed once over TLS, encrypted
  // at rest server-side, and never returned to the client afterward.
  async addBankLink(input: {
    institutionId: string;
    institutionName: string;
    username: string;
    password: string;
  }): Promise<PrimeAgentBankLink> {
    return request<PrimeAgentBankLink>("/prime-agent/bank-links", {
      method: "POST",
      body: JSON.stringify({
        institution_id: input.institutionId,
        institution_name: input.institutionName,
        username: input.username,
        password: input.password,
      }),
    });
  },

  async deleteBankLink(id: string): Promise<{ success: boolean }> {
    return request<{ success: boolean }>(`/prime-agent/bank-links/${id}`, {
      method: "DELETE",
    });
  },

  // Request-driven scrape: Focus asks Prime Agent to log in and pull transactions now.
  async syncBankLink(id: string): Promise<PrimeAgentSyncResult> {
    return request<PrimeAgentSyncResult>(`/prime-agent/bank-links/${id}/sync`, {
      method: "POST",
    });
  },

  // Relay a 2FA code back to Prime Agent to finish a sync that returned needs_otp.
  async submitOtp(id: string, code: string): Promise<PrimeAgentSyncResult> {
    return request<PrimeAgentSyncResult>(`/prime-agent/bank-links/${id}/sync/otp`, {
      method: "POST",
      body: JSON.stringify({ code }),
    });
  },
};

export default primeAgentService;
